import { handleActions } from "redux-actions";
import { OrderedSet } from "immutable";
import { CART_GRID_VIEW } from "constants/action-types";
import { ReducerMap } from "./types";
import { ActionDef } from "actions/cart-grid-view";
import { GridViewColumnState, GridViewStoredState } from "helpers/porduct-collection-types";
import { updateStateActions } from "reducers/base-reducers";
import { GridBuffer } from "constants/sizingView";
import { ICompany } from "components/contacts/contacts-types";
import { ICartAvailability } from "types/availability";
import { IViewDefinition } from "types/view-definitions";
import { IWorkingOrder } from "types/cart";
import { ILocation, VIEW_MODE } from "types/cart/shipment-review";
import { ILineItem } from "constants/order-types";
import { IWorkingOrderPricing } from "types/cart/pricing";
import { IInventoryMap } from "types/inventory";
import type { IProduct } from "types/product";

export interface IPivot {
    mode?: boolean;
    columns?: string[];
}

export interface CartGridView {
    isActive: boolean;
    isLoading: boolean;
    rowIds: OrderedSet<string>;
    selectedRows: OrderedSet<string>;
    selectedItems: Array<Pick<ILineItem, "_id" | "product">>;
    buffer: GridBuffer;
    expandedSizes: OrderedSet<string>;
    searchText: string;
    activeView: Nullish<string>;
    viewMode: VIEW_MODE;
    customViews?: ICustomView[];
    gridState: GridViewStoredState;
    data: ICartData;
}

export type IGridSettings = Partial<CartGridView>;

export interface ICustomView {
    _id?: string;
    name: string;
    isDefault: boolean;
    columns: Partial<GridViewColumnState>[];
    filters: { [key: string]: any };
    pivot: IPivot;
    viewMode: VIEW_MODE;
}

export interface ICartData {
    order?: IWorkingOrder;
    availability?: ICartAvailability;
    products?: IProduct[];
    company?: ICompany;
    locations?: ILocation[];
    itemView?: IViewDefinition;
    inventory?: ILegacyInventory;
    productWarehouses?: { [productId: string]: { default: string; available: string[] } };
    pricing?: IWorkingOrderPricing;
    replenishments?: IInventoryMap;
    showFieldsError?: boolean;
}

// legacy structure of inventory to retrieve data by size name
export interface ILegacyInventory {
    [product: string]: {
        [skuId: string]: {
            size: string;
            inventory: {
                [key: string]: {
                    sku_id: string;
                    bucket: Date;
                    quantity: number;
                    display_quantity: string;
                    warehouse: string;
                };
            };
        };
    };
}

export const initialState: CartGridView = {
    isActive: false,
    isLoading: false,
    rowIds: OrderedSet<string>(),
    selectedRows: OrderedSet<string>(),
    selectedItems: [],
    buffer: { context: null, entries: [] },
    expandedSizes: OrderedSet<string>(),
    searchText: "",
    activeView: undefined,
    viewMode: VIEW_MODE.INITIAL,
    gridState: {},
    data: {},
};

const reducerMap: ReducerMap<ActionDef, CartGridView> = {
    [CART_GRID_VIEW.ROWIDS]: (state, { payload: ids }) =>
        state.rowIds.equals(ids)
            ? state
            : {
                  ...state,
                  rowIds: OrderedSet(ids),
              },
    [CART_GRID_VIEW.SELECTED.SET]: (state, { payload: { ids, selectedItems } }) =>
        state.selectedRows.equals(ids)
            ? state
            : {
                  ...state,
                  selectedRows: OrderedSet(ids),
                  selectedItems,
              },
    [CART_GRID_VIEW.SELECTED.DESELECT_ALL]: (state) => ({
        ...state,
        selectedRows: OrderedSet(),
    }),
    [CART_GRID_VIEW.SELECTED.SHOW]: (state) => ({
        ...state,
        expandedSizes: state.expandedSizes.union(state.selectedRows),
    }),
    [CART_GRID_VIEW.SELECTED.HIDE]: (state) => ({
        ...state,
        expandedSizes: state.expandedSizes.subtract(state.selectedRows),
    }),
    [CART_GRID_VIEW.SIZES_SHOWN_SET]: (state, { payload: ids }) =>
        state.expandedSizes.equals(ids)
            ? state
            : {
                  ...state,
                  expandedSizes: OrderedSet(ids),
              },
    [CART_GRID_VIEW.LOADING]: (state, { payload: isLoading }) => ({
        ...state,
        isLoading,
    }),
    [CART_GRID_VIEW.SEARCH_TEXT]: (state, { payload: searchText }) => ({
        ...state,
        searchText,
    }),
    [CART_GRID_VIEW.CLEAR]: () => ({
        ...initialState,
    }),
    [CART_GRID_VIEW.GRID_STATE]: (state, { payload = {} }) => ({
        ...state,
        gridState: {
            ...state.gridState,
            ...payload,
        },
    }),

    [CART_GRID_VIEW.CUSTOM_VIEWS.SET]: (state, { payload }: { payload: ICustomView[] }) => ({
        ...state,
        customViews: payload,
    }),
};

export default handleActions<CartGridView, any>(
    {
        ...reducerMap,
        ...updateStateActions<CartGridView>(CART_GRID_VIEW),
    } as any,
    initialState,
);
